import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FAQS } from "@/data/faqs";

export default function Faqs() {
  const categories = useMemo(() => ["All", ...new Set(FAQS.map((f) => f.category))], []);
  const [active, setActive] = useState("All");
  const visible = active === "All" ? FAQS : FAQS.filter((f) => f.category === active);

  return (
    <main data-testid="faqs-page">
      <Seo
        title="No Retainers, Fixed Fees, Same-Day Answers, HR Consultancy FAQs | Electra HR"
        siteName="Electra HR"
        description="Straight answers on fees, response times, and legal escalation, no waffle, no retainer. See exactly how UK HR consultancy pricing and support actually works."
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
                { "@type": "ListItem", position: 2, name: "FAQs", item: `${window.location.origin}/faqs` },
              ],
            },
          ],
        }}
      />
      <section className="grain bg-ink pb-16 pt-32 sm:pt-40 text-paper">
        <div className="container-x">
          <p className="eyebrow text-pink">FAQs</p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            Asked, answered, no spin.
          </h1>
        </div>
      </section>
      <section className="bg-paper py-16 sm:py-24">
        <div className="container-x max-w-4xl">
          <div className="flex flex-wrap gap-3" role="tablist" aria-label="FAQ categories">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`faq-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`border-2 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors ${
                  active === c ? "border-ink bg-ink text-paper" : "border-ink/20 text-ink/60 hover:border-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <Reveal className="mt-10">
            {/* Each question is a heading with its answer directly under it, always
                in the page, so the visible text matches the FAQPage data. */}
            <div className="divide-y divide-ink/15 border-y border-ink/15">
              {visible.map((f, i) => (
                <section key={f.q} className="py-6" data-testid={`faq-item-${i}`}>
                  <h2 data-testid={`faq-question-${i}`} className="font-display text-base sm:text-lg font-semibold tracking-tight text-ink">
                    {f.q}
                  </h2>
                  <div data-testid={`faq-answer-${i}`} className="mt-2 text-sm sm:text-base leading-relaxed text-ink/70">
                    <p>{f.a}</p>
                    {f.link && (
                      <Link to={f.link.to} data-testid={`faq-link-${i}`} className="mt-3 inline-flex font-semibold underline decoration-pink decoration-2 underline-offset-4 transition-colors hover:text-pink">
                        {f.link.label}
                      </Link>
                    )}
                  </div>
                </section>
              ))}
            </div>
          </Reveal>
          <div className="mt-14 border-2 border-ink bg-pink/15 p-8">
            <p className="font-display text-xl font-semibold">Question not here?</p>
            <p className="mt-2 text-sm text-ink/70">Ask it directly. A human replies within one working day.</p>
            <Link to="/contact" data-testid="faqs-cta-contact" className="group mt-5 inline-flex items-center gap-2 bg-ink px-6 py-3 font-bold text-paper transition-transform duration-300 hover:-translate-y-0.5">
              Ask us <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
